import { compareAsc, compareDesc } from 'date-fns';

import { Processes as Process, StepName } from '../design-system/types';
import {
    DisplayDossierOverview,
    DossierOverview,
    TaskDefinition,
} from '../resources/dossier/types';

import { ActionTypes } from './actionType';
import { Processes, ProcessInArray } from './process';

const getNestedProperty = <T>(obj: DisplayDossierOverview, path: string[]): T | undefined => {
    // @ts-expect-error Dynamic nested property access via string path on typed object
    return path.reduce((current, key) => current?.[key], obj);
};

export function orderByNumberDesc(objectPath: string) {
    return function (a: DisplayDossierOverview, b: DisplayDossierOverview): number {
        const aNumber = getNestedProperty<string>(a, objectPath.split('.'));
        const bNumber = getNestedProperty<string>(b, objectPath.split('.'));
        if (!aNumber || !bNumber) {
            return 0;
        }
        return parseInt(bNumber, 10) - parseInt(aNumber, 10);
    };
}

export function orderByTask(tasks: Array<string | string[]>) {
    return function (a: DisplayDossierOverview, b: DisplayDossierOverview): number {
        const getIndexOfTask = (taskDefinition: TaskDefinition) =>
            tasks
                .map((task, index) => (task.includes(taskDefinition) ? index : -1))
                .filter((index) => index >= 0);
        const [aIndexTaskDefinition] = getIndexOfTask(a.task.definition);
        const [bIndexTaskDefinition] = getIndexOfTask(b.task.definition);
        return aIndexTaskDefinition - bIndexTaskDefinition;
    };
}

export function orderByDate(objectPath: string, asc = true) {
    return function (a: DisplayDossierOverview, b: DisplayDossierOverview): number {
        const aDate = getNestedProperty<Date>(a, objectPath.split('.'));
        const bDate = getNestedProperty<Date>(b, objectPath.split('.'));
        if (aDate && bDate) {
            return asc ? compareAsc(aDate, bDate) : compareDesc(aDate, bDate);
        }
        return 0;
    };
}

export function orderOnlyNewDossierByDate(objectPath: string, asc = true) {
    return function (a: DisplayDossierOverview, b: DisplayDossierOverview): number {
        const aDate = getNestedProperty<Date>(a, objectPath.split('.'));
        const bDate = getNestedProperty<Date>(b, objectPath.split('.'));
        if (
            a.discussion.lastAgentToTravellerMessageDate === null ||
            b.discussion.lastAgentToTravellerMessageDate === null
        ) {
            if (aDate && bDate) {
                return asc ? compareAsc(aDate, bDate) : compareDesc(aDate, bDate);
            }
        }
        return 0;
    };
}

export function orderByActionType(a: DisplayDossierOverview, b: DisplayDossierOverview): number {
    return ActionTypes.indexOf(a.task.actionType) - ActionTypes.indexOf(b.task.actionType);
}

export function orderByNewDossier() {
    return function (a: DisplayDossierOverview, b: DisplayDossierOverview): number {
        if (
            a.discussion.lastAgentToTravellerMessageDate &&
            b.discussion.lastAgentToTravellerMessageDate === null
        ) {
            return 1;
        }
        if (
            a.discussion.lastAgentToTravellerMessageDate === null &&
            b.discussion.lastAgentToTravellerMessageDate
        ) {
            return -1;
        }
        return 0;
    };
}

export function orderByHeatLevel() {
    return (a: DisplayDossierOverview, b: DisplayDossierOverview): number =>
        b.heatLevel - a.heatLevel;
}

export const getProcessFrom = (step: StepName): Process =>
    ProcessInArray.filter((process) => Processes[process].includes(step))[0];

export const isDossierArchived = (dossier: DossierOverview): boolean =>
    Processes.archive.includes(dossier.step);
